<template>
  <NextButton
    v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_COPY_MESSAGES')"
    :icon="isCopyingMessages ? '' : 'i-ph-copy'"
    :class="{ 'spinner': isCopyingMessages }"
    :disabled="isCopyingMessages"
    slate
    faded
    sm
    @click="copyAllMessages"
  />
</template>

<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import { mapGetters } from 'vuex';

export default {
  name: 'CopyMessagesButton',
  components: { NextButton },
  data() {
    return {
      isCopyingMessages: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
  },
  methods: {
    async copyAllMessages() {
      if (this.isCopyingMessages) return; // Предотвращаем множественные клики
      
      this.isCopyingMessages = true;
      
      try {
        // Загружаем все сообщения из истории
        const allMessages = await this.fetchAllMessagesForCopy();
        
        if (allMessages.length === 0) {
          useAlert(this.$t('CONVERSATION.REPLYBOX.COPY_MESSAGES_ERROR'));
          return;
        }

        // Форматируем сообщения для копирования
        const formattedMessages = allMessages
          .filter(message => !message.private) // Исключаем приватные заметки
          .map(message => {
            let senderName = '';
            let senderRole = '';
            
            // Определяем роль на основе sender_type и message_type
            if (message.sender_type === 'Contact' || message.message_type === 0) {
              // Контакт (клиент)
              senderRole = `${this.$t('CONVERSATION.CUSTOMER') || 'Customer'}`;
            } else if (message.sender_type === 'User' || message.message_type === 1) {
              // Пользователь (агент)
              senderRole = `${this.$t('CONVERSATION.AGENT') || 'Agent'}`;
            } else if (message.sender_type === 'AgentBot') {
              // Бот
              senderRole = `${this.$t('CONVERSATION.BOT') || 'Bot'}`;
            } else if (message.message_type === 2) {
              // Системное сообщение (активность)
              senderRole = `${this.$t('CONVERSATION.BOT') || 'System'}`;
            } else {
              // Fallback для неопределенных типов
              senderRole = '(Unknown)';
            }
            
            senderName = message.sender?.name || senderRole;

            const sender = `${senderName}(${senderRole})`;
            const timestamp = new Date(message.created_at * 1000).toLocaleString();
            let content = message.content || '';
            
            // Если есть вложения, добавляем информацию о них
            if (message.attachments && message.attachments.length > 0) {
              const attachmentInfo = message.attachments.map(att => 
                `[${this.$t('CONVERSATION.ATTACHMENT') || 'File'}: ${att.data_url ? att.data_url.split('/').pop() : 'file'}]`
              ).join(', ');
              content = content ? `${content} ${attachmentInfo}` : attachmentInfo;
            }
            
            if (!content) {
              content = `[${this.$t('CONVERSATION.NO_CONTENT') || 'No content'}]`;
            }
            
            return `[${timestamp}] ${sender}: ${content}`;
          })
          .join('\n\n');

        if (!formattedMessages) {
          useAlert(this.$t('CONVERSATION.REPLYBOX.COPY_MESSAGES_ERROR'));
          return;
        }

        // Копируем в буфер обмена используя современный API
        await navigator.clipboard.writeText(formattedMessages);
        useAlert(this.$t('CONVERSATION.REPLYBOX.COPY_MESSAGES_SUCCESS'));
      } catch (error) {
        console.error('Error copying messages:', error);
        useAlert(this.$t('CONVERSATION.REPLYBOX.COPY_MESSAGES_ERROR'));
      } finally {
        this.isCopyingMessages = false;
      }
    },
    
    async fetchAllMessagesForCopy() {
      try {
        const conversationId = this.currentChat.id;
        let allMessages = [...(this.currentChat.messages || [])];
        let hasMoreMessages = true;
        let oldestMessageId = allMessages.length > 0 ? allMessages[0].id : null;
        
        // Загружаем сообщения порциями, пока не получим все
        while (hasMoreMessages && oldestMessageId) {
          const response = await MessageApi.getPreviousMessages({
            conversationId,
            before: oldestMessageId
          });
          
          const newMessages = response.data.payload || [];
          
          if (newMessages.length === 0) {
            hasMoreMessages = false;
          } else {
            // Добавляем новые сообщения в начало массива (они идут в обратном порядке)
            allMessages = [...newMessages, ...allMessages];
            oldestMessageId = newMessages[0].id;
          }
        }
        
        // Сортируем сообщения по времени создания (от старых к новым)
        return allMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      } catch (error) {
        console.error('Error fetching all messages:', error);
        // В случае ошибки возвращаем только текущие сообщения
        return this.currentChat.messages || [];
      }
    },
  },
};
</script> 
